import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"

import { useGlAccountOptions } from "../../hooks/useGlAccountOptions"
import type { GlAccountFormValues } from "../../schemas/glAccountForm"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"

interface ParentAccountAutocompleteProps {
  initialOption?: { value: string; label: string } | null
}

export const ParentAccountAutocomplete: FC<ParentAccountAutocompleteProps> = ({ initialOption }) => {
  const { control, setValue } = useFormContext<GlAccountFormValues>()

  const { glAccounts: parentAccounts, setSearchTerm } = useGlAccountOptions({ pause: false })

  // Track the last selected option so it stays in the list even if a subsequent
  // search query returns results that don't include it (race condition guard).
  const [selectedParentOption, setSelectedParentOption] = useState<{ value: string; label: string } | null>(
    initialOption ?? null
  )

  const options = useMemo(() => {
    const mapped = parentAccounts.map((account) => ({ value: account.id, label: account.label }))

    if (selectedParentOption && !mapped.some((o) => o.value === selectedParentOption.value)) {
      mapped.unshift(selectedParentOption)
    }

    return mapped
  }, [parentAccounts, selectedParentOption])

  // Auto-populate Type, Subtype, and Classification from the selected parent
  const handleChange = useCallback(
    (value: string | null) => {
      if (!value) {
        setSelectedParentOption(null)
        setValue("type", null, { shouldValidate: true, shouldDirty: true })
        setValue("subType", null, { shouldValidate: true, shouldDirty: true })
        setValue("classification", null, { shouldValidate: true, shouldDirty: true })
        return
      }

      const selectedParent = parentAccounts.find((account) => account.id === value)

      if (selectedParent) {
        setSelectedParentOption({ value: selectedParent.id, label: selectedParent.label })

        setValue("type", selectedParent.metadata?.type, { shouldValidate: true, shouldDirty: true })
        setValue("subType", selectedParent.metadata?.subType, { shouldValidate: true, shouldDirty: true })
        setValue("classification", selectedParent.metadata?.classification, { shouldValidate: true, shouldDirty: true })
      }
    },
    [parentAccounts, setValue]
  )

  return (
    <FormAutocomplete
      control={control}
      name='parentGlAccountId'
      label='Parent Account'
      placeholder='Search by account number or title'
      options={options}
      required
      className='col-span-2'
      data-testid='parent-account-input'
      onChange={handleChange}
      onInputChange={setSearchTerm}
      filterOptions={(x) => x}
    />
  )
}
